import { msg } from "@lingui/core/macro";
import { rootTransitionKey } from "@/lib/root-transition-key";
import { createRootRoute, Outlet, useLocation } from "@tanstack/solid-router";
import { createEffect, createSignal, onCleanup, onMount, Show, Suspense } from "solid-js";
import { Toaster } from "@/components/ui/toast";
import { AppErrorBoundary } from "@/components/ui/error-boundary";
import { InitialSyncOverlay } from "@/components/initial-sync-overlay";
import { AlertCircle, RefreshCw } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { BootManager } from "@/providers/boot-manager";
import { AppLockProvider, appState, GlassSurface, PageTransition, Transition } from "@fleet/ui";
import { AppBackground } from "@/components/app-background";
import { FullPageLoader } from "@/components/full-page-loader";
import { installNewTabAccountHandoff } from "@/lib/new-tab-handoff";

function RootErrorComponent(props: { error: unknown; reset: () => void }) {
  const { t } = useI18n();
  createEffect(() => {
    console.error("[RootErrorComponent] Caught route error:", props.error);
  });
  return (
    <div class="min-h-dvh flex items-center justify-center p-4 bg-background">
      <GlassSurface class="max-w-md w-full p-8 flex flex-col items-center text-center space-y-6">
        <div class="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertCircle class="h-8 w-8 text-destructive" />
        </div>
        <div class="space-y-2">
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Something went wrong`)}</h2>
          <p class="text-muted-foreground text-sm">
            {props.error instanceof Error
              ? props.error.message
              : t(msg`An unexpected error has occurred.`)}
          </p>
        </div>
        <div class="flex gap-3 w-full">
          <Button
            variant="outline"
            class="flex-1"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            {t(msg`Reload page`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

const SKELETON_DELAY_MS = 150;

function RootComponent() {
  const location = useLocation();
  const [showSkeleton, setShowSkeleton] = createSignal(false);

  onMount(() => {
    const timer = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS);
    onCleanup(() => {
      clearTimeout(timer);
    });
  });

  return (
    <div class="relative min-h-dvh w-full">
      {/* High-fidelity persistent workspace skeleton backdrop */}
      <Transition name="fade">
        <Show when={showSkeleton() && !appState.isBootComplete()}>
          <div class="fixed inset-0 z-[70] bg-background">
            <FullPageLoader />
          </div>
        </Show>
      </Transition>

      <Suspense fallback={null}>
        <AppErrorBoundary label="root">
          <AppBackground class="flex flex-col min-h-dvh">
            <BootManager>
              {/* OPTIONAL biometric app-lock: inside the boot gate so it guards
                  only once the app is ready. Pure pass-through unless the user
                  opted in AND on native AND biometrics are available. */}
              <AppLockProvider>
                <InitialSyncOverlay>
                  <PageTransition
                    routeKey={rootTransitionKey(location().pathname)}
                    class="flex flex-1 flex-col min-h-0 h-full"
                  >
                    <Outlet />
                  </PageTransition>
                </InitialSyncOverlay>
              </AppLockProvider>
            </BootManager>
          </AppBackground>
        </AppErrorBoundary>
        <Toaster />
      </Suspense>
    </div>
  );
}

export const rootRoute = createRootRoute({
  beforeLoad: () => {
    installNewTabAccountHandoff();
  },
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
